import AuthService from "../../../api/services/AuthService";
import { persistDeviceIdFromServer } from "../../../utils/deviceId";

/**
 * POST /v1/user/check-signup-email — allow/reject from `success` only.
 * Login:  success true → User found; false → User not found
 * Signup: success true → available; false → Email/Phone already exists
 */
export const parseIdentifierCheckResponse = (res, purpose = "signup") => {
  const isLogin = String(purpose || "signup").trim().toLowerCase() === "login";

  if (res == null) {
    return { ok: false, exists: null, code: null, message: "No response from server." };
  }

  const referral = res.referral;
  if (
    referral &&
    typeof referral === "object" &&
    referral.success === false
  ) {
    return {
      ok: false,
      exists: null,
      code: referral.code || "INVALID_REFERRAL_CODE",
      message: referral.message || "Referral code is invalid.",
    };
  }

  if (res.success === true) {
    return { ok: true, exists: isLogin, code: null, message: res.message || null };
  }

  return {
    ok: false,
    exists: isLogin ? false : true,
    code: res.code || null,
    message:
      res.message ||
      (isLogin ? "User not found" : "Email already exists"),
  };
};

export const parseSignupAvailabilityCheckResponse = (res, fallbackMessage = "This identifier cannot be used for signup.") => {
  const parsed = parseIdentifierCheckResponse(res, "signup");
  if (!parsed.ok && fallbackMessage) {
    return { ok: false, code: parsed.code, message: parsed.message || fallbackMessage };
  }
  return { ok: parsed.ok, code: parsed.code, message: parsed.message };
};

export const parseSignupEmailCheckResponse = (res) =>
  parseIdentifierCheckResponse(res, "signup");

export const parseSignupPhoneCheckResponse = (res) =>
  parseIdentifierCheckResponse(res, "signup");

export const parseLoginIdentifierCheckResponse = (res) =>
  parseIdentifierCheckResponse(res, "login");

/** Extract JWT from various backend response shapes. */
export const pickAuthTokenFromResult = (result) => {
  if (!result || typeof result !== "object") return null;
  const d = result.data;
  if (typeof d === "string" && d.trim().length > 0) {
    return d.replace(/^Bearer\s+/i, "").trim();
  }
  const candidates = [
    result.token, result.accessToken, result.access_token,
    d?.token, d?.accessToken, d?.access_token, d?.jwt,
    d?.bearerToken, d?.authToken, d?.sessionToken, d?.id_token,
    d && typeof d === "object" && (d.data?.token || d.data?.accessToken),
    result?.result?.token, d?.result?.token,
    d?.user?.token, d?.user?.accessToken,
    d?.auth?.token, d?.auth?.access_token,
  ];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== "string") continue;
    const t = raw.replace(/^Bearer\s+/i, "").trim();
    if (t) return t;
  }
  if (d && typeof d === "object" && !Array.isArray(d)) {
    for (const v of Object.values(d)) {
      if (typeof v === "string" && v.length > 40 && v.startsWith("eyJ") && v.includes(".")) {
        return v.replace(/^Bearer\s+/i, "").trim();
      }
    }
  }
  return null;
};

export const persistAuthTokenFromSignupResult = (result) => {
  const token = pickAuthTokenFromResult(result);
  const d = result?.data && typeof result.data === "object" ? result.data : {};
  const refresh = d.refresh_token || d.refreshToken || result?.refresh_token || result?.refreshToken || null;
  if (token) {
    localStorage.setItem("token", token);
    window.dispatchEvent(new Event("agce-auth-changed"));
    window.dispatchEvent(new Event("agce-auth-token-changed"));
  }
  if (refresh) {
    localStorage.setItem("refresh_token", String(refresh).trim());
  }
  if (d.device_id) persistDeviceIdFromServer(d.device_id);
  const userId = result?.data?.userId ?? result?.data?.id ?? result?.userId;
  if (userId != null && userId !== "") {
    localStorage.setItem("userId", String(userId));
  }
};

export const getLoginIdentifierForSignup = (accountTab, signId) => {
  if (accountTab === "email") return String(signId || "").trim();
  return String(signId || "").replace(/\D/g, "").replace(/^0+/, "") || "";
};

export const tryObtainSessionViaLoginAfterOtp = async (accountTab, signId, password) => {
  if (localStorage.getItem("token")) return;
  const loginId = getLoginIdentifierForSignup(accountTab, signId);
  if (!loginId || !password) return;
  const loginRes = await AuthService.login(loginId, password, "");
  if (loginRes?.success) {
    persistAuthTokenFromSignupResult(loginRes);
  }
};

export const getQueryParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch {
    return "";
  }
};

export const isEntitySignupParam = (value) => value === "1" || value === "true";

/** Individual and business sign-ups both use the KYC VARA form (the user picks Corporate there). */
export const getSignupVerificationPath = (isEntitySignup) => "/kyc-verification";
